/**
 * KCIS Intelligence Layer — Admin Console (Phase 1).
 * Route: /admin/intelligence  (super_admin only)
 *
 * Schema-driven CRUD over 6 libraries:
 *   • Symptoms
 *   • Body Systems
 *   • Nutrient Deficiencies
 *   • Conditions
 *   • Herbs
 *   • Protocol Rules
 *
 * The page does NOT hardcode field knowledge — it pulls field metadata from
 * `GET /api/intelligence/libraries` and renders inputs accordingly. Adding a
 * new library on the backend automatically surfaces a new tab here.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Plus, Save, Trash2, Loader2, RefreshCw, Upload, Download, X, Brain,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { TopNav } from "@/components/Layout";
import { adminAuth } from "@/lib/api";
import { intelligenceApi } from "@/lib/intelligenceApi";
import { formatApiError } from "@/lib/formatError";

// Field names we never expose in the editor — they're system-managed.
const HIDDEN = new Set(["slug", "created_at", "updated_at", "created_by", "updated_by"]);

export default function AdminIntelligence() {
  const navigate = useNavigate();
  const [libs, setLibs] = useState([]);
  const [active, setActive] = useState("symptoms");
  const [health, setHealth] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create"); // "create" | "edit"
  const [editorDraft, setEditorDraft] = useState({});
  const [editorSaving, setEditorSaving] = useState(false);
  const [importerOpen, setImporterOpen] = useState(false);
  const [importerText, setImporterText] = useState("");
  const [importing, setImporting] = useState(false);
  // Cross-reference look-ups keyed by library key.
  const [refOptions, setRefOptions] = useState({}); // { 'symptoms': [{slug,name},...] }

  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    if (!adminAuth.isSuperAdmin()) { toast.error("Super admin access required."); navigate("/admin"); return; }
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bootstrap = async () => {
    try {
      const r = await intelligenceApi.libraries();
      setLibs(r.data.libraries);
    } catch (err) {
      if ([401, 403].includes(err?.response?.status)) { adminAuth.clear(); navigate("/admin/login"); return; }
      toast.error(formatApiError(err, "Failed to load library metadata"));
    }
  };

  useEffect(() => {
    if (!libs.length) return;
    refreshHealth();
    fetchList();
    // Pre-load ref-option pools so cross-reference multi-selects work fast.
    Promise.all(libs.map(async (lib) => {
      try {
        const r = await intelligenceApi.list(lib.key, { page_size: 500 });
        return [lib.key, r.data.items.map((it) => ({ slug: it.slug, name: it.name }))];
      } catch { return [lib.key, []]; }
    })).then((pairs) => setRefOptions(Object.fromEntries(pairs)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libs, active]);

  const refreshHealth = async () => {
    try {
      const r = await intelligenceApi.healthSummary();
      setHealth(r.data.counts);
    } catch { /* */ }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await intelligenceApi.list(active, { q: q || undefined, page: 1, page_size: 200 });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load entries"));
    } finally {
      setLoading(false);
    }
  };

  const currentLib = useMemo(() => libs.find((l) => l.key === active), [libs, active]);
  const editableFields = useMemo(() => (currentLib?.fields || []).filter((f) => !HIDDEN.has(f.name)), [currentLib]);

  const openCreate = () => {
    const blank = {};
    (currentLib?.fields || []).forEach((f) => {
      if (HIDDEN.has(f.name)) return;
      blank[f.name] = f.type === "list" ? [] : (f.name === "is_archived" ? false : "");
    });
    setEditorDraft(blank);
    setEditorMode("create");
    setEditorOpen(true);
  };

  const openEdit = (item) => {
    setEditorDraft(JSON.parse(JSON.stringify(item)));
    setEditorMode("edit");
    setEditorOpen(true);
  };

  const saveEditor = async () => {
    setEditorSaving(true);
    try {
      if (editorMode === "create") {
        await intelligenceApi.create(active, editorDraft);
        toast.success("Created");
      } else {
        await intelligenceApi.update(active, editorDraft.slug, editorDraft);
        toast.success("Saved");
      }
      setEditorOpen(false);
      await fetchList();
      await refreshHealth();
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    } finally {
      setEditorSaving(false);
    }
  };

  const archiveItem = async (item) => {
    if (!window.confirm(`Archive '${item.name}'?`)) return;
    try {
      await intelligenceApi.archive(active, item.slug, false);
      toast.success("Archived");
      await fetchList(); await refreshHealth();
    } catch (err) {
      toast.error(formatApiError(err, "Archive failed"));
    }
  };

  const runImport = async () => {
    let parsed;
    try {
      parsed = JSON.parse(importerText);
      if (!Array.isArray(parsed)) throw new Error("Expected a JSON array of items.");
    } catch (err) {
      toast.error(`Invalid JSON: ${err.message}`);
      return;
    }
    setImporting(true);
    try {
      const r = await intelligenceApi.importJson(active, { items: parsed, upsert: true, dry_run: false });
      const s = r.data.summary;
      toast.success(`Imported ${s.created} new, ${s.updated} updated, ${s.errors} errors`);
      setImporterOpen(false);
      setImporterText("");
      await fetchList(); await refreshHealth();
    } catch (err) {
      toast.error(formatApiError(err, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f5]" data-testid="admin-intelligence">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/admin" className="text-xs text-slate-500 hover:text-purple-900 inline-flex items-center">
              <ChevronLeft className="w-3.5 h-3.5 mr-0.5" /> Back to admin
            </Link>
            <h1 className="mt-1 font-display text-3xl text-slate-900 flex items-center gap-3">
              <Brain className="w-6 h-6 text-purple-900" />
              KCIS Intelligence Layer
            </h1>
            <p className="text-sm text-slate-600">Phase 1 · data architecture · 6 master libraries + protocol rule engine</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { refreshHealth(); fetchList(); }} data-testid="intel-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Health summary tiles */}
        {health && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="intel-health">
            {libs.map((lib) => {
              const h = health[lib.key];
              if (!h) return null;
              return (
                <button
                  key={lib.key}
                  onClick={() => setActive(lib.key)}
                  className={`text-left rounded-xl border p-3 transition ${
                    active === lib.key
                      ? "border-purple-900 bg-purple-50 ring-2 ring-purple-300"
                      : "border-slate-200 bg-white hover:border-purple-300"
                  }`}
                  data-testid={`intel-tile-${lib.key}`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-purple-900 font-semibold">{lib.label}</div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900">{h.active}</div>
                  <div className="text-[11px] text-slate-500">total {h.total} · archived {h.archived}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Library tabs */}
        <Tabs value={active} onValueChange={setActive} data-testid="intel-tabs">
          <TabsList className="flex-wrap h-auto">
            {libs.map((lib) => (
              <TabsTrigger key={lib.key} value={lib.key} data-testid={`intel-tab-${lib.key}`}>
                {lib.label.replace(" Library", "").replace("Nutrient ", "").replace(" Intelligence", "")}
              </TabsTrigger>
            ))}
          </TabsList>

          {libs.map((lib) => (
            <TabsContent key={lib.key} value={lib.key} className="mt-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); fetchList(); }}>
                  <Input
                    placeholder="search…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="w-64"
                    data-testid="intel-search"
                  />
                  <Button type="submit" size="sm" variant="outline">Search</Button>
                </form>
                <div className="ml-auto flex items-center gap-2">
                  <Button size="sm" onClick={openCreate} data-testid="intel-create">
                    <Plus className="w-4 h-4 mr-1" /> New
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setImporterOpen(true)} data-testid="intel-import">
                    <Upload className="w-4 h-4 mr-1" /> Import JSON
                  </Button>
                  <a href={intelligenceApi.exportJsonUrl(active)} target="_blank" rel="noreferrer" data-testid="intel-export">
                    <Button size="sm" variant="outline" type="button">
                      <Download className="w-4 h-4 mr-1" /> Export
                    </Button>
                  </a>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="font-mono">Slug</TableHead>
                      <TableHead>Category / Type</TableHead>
                      <TableHead>References</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</TableCell></TableRow>
                    ) : items.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">No entries. Create your first one or import.</TableCell></TableRow>
                    ) : items.map((item) => {
                      const refCount = (lib.fields || [])
                        .filter((f) => f.ref && Array.isArray(item[f.name]))
                        .reduce((acc, f) => acc + (item[f.name]?.length || 0), 0);
                      return (
                        <TableRow key={item.slug} data-testid={`intel-row-${item.slug}`}>
                          <TableCell className="font-medium">
                            {item.name}
                            {item.is_archived && <Badge variant="outline" className="ml-2 text-[10px]">archived</Badge>}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-slate-500">{item.slug}</TableCell>
                          <TableCell className="text-sm text-slate-700">{item.category || item.scientific_name || "—"}</TableCell>
                          <TableCell className="text-sm">
                            <Badge variant="outline" className="font-mono">{refCount}</Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="sm" variant="outline" onClick={() => openEdit(item)} data-testid={`intel-edit-${item.slug}`}>Edit</Button>
                            {!item.is_archived && (
                              <Button size="sm" variant="ghost" onClick={() => archiveItem(item)} data-testid={`intel-archive-${item.slug}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="p-3 border-t border-slate-100 text-xs text-slate-500">Total: {total}</div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </main>

      {/* Editor */}
      <Sheet open={editorOpen} onOpenChange={setEditorOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="intel-editor">
          <SheetHeader>
            <SheetTitle>
              {editorMode === "create" ? "Create entry" : "Edit entry"} · {currentLib?.label}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-5 space-y-4">
            {editableFields.map((field) => (
              <FieldEditor
                key={field.name}
                field={field}
                value={editorDraft[field.name]}
                onChange={(v) => setEditorDraft({ ...editorDraft, [field.name]: v })}
                refOptions={refOptions}
              />
            ))}
            <div className="pt-4 flex items-center gap-2">
              <Button onClick={saveEditor} disabled={editorSaving} data-testid="intel-save">
                {editorSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
              <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Importer */}
      <Sheet open={importerOpen} onOpenChange={setImporterOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="intel-importer">
          <SheetHeader>
            <SheetTitle>Bulk import · {currentLib?.label}</SheetTitle>
          </SheetHeader>
          <p className="mt-3 text-sm text-slate-600">
            Paste a JSON array of entries. Slugs are auto-derived from <code>name</code> if missing.
            Upsert is on by default — existing entries are merged.
          </p>
          <Textarea
            value={importerText}
            onChange={(e) => setImporterText(e.target.value)}
            rows={12}
            className="mt-3 font-mono text-xs"
            placeholder='[{"name":"Iron","food_sources":["red meat","lentils"]}]'
            data-testid="intel-import-textarea"
          />
          <div className="mt-4 flex items-center gap-2">
            <Button onClick={runImport} disabled={importing} data-testid="intel-import-run">
              {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Import
            </Button>
            <Button variant="outline" onClick={() => setImporterOpen(false)}>Cancel</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function FieldEditor({ field, value, onChange, refOptions }) {
  const fname = field.name;
  const labelText = fname.replace(/_/g, " ").replace(/\bslugs\b/, "→").trim();
  // Cross-reference multi-select (slug list pointing at another library)
  if (field.type === "list" && field.ref) {
    const options = refOptions[field.ref] || [];
    const chosen = Array.isArray(value) ? value : [];
    const toggle = (slug) => {
      if (chosen.includes(slug)) onChange(chosen.filter((s) => s !== slug));
      else onChange([...chosen, slug]);
    };
    return (
      <div>
        <Label className="capitalize">{labelText} <span className="text-xs text-slate-400">(refs {field.ref})</span></Label>
        <div className="mt-1 flex flex-wrap gap-1.5 p-2 border border-slate-200 rounded-lg bg-white max-h-44 overflow-y-auto">
          {options.length === 0 ? (
            <div className="text-xs text-slate-500 py-1">No entries in <code>{field.ref}</code> yet.</div>
          ) : options.map((o) => (
            <button
              key={o.slug}
              type="button"
              onClick={() => toggle(o.slug)}
              className={`px-2 py-0.5 rounded-full text-xs border transition ${
                chosen.includes(o.slug)
                  ? "bg-purple-900 text-white border-purple-900"
                  : "bg-white text-slate-700 border-slate-300 hover:border-purple-300"
              }`}
            >
              {o.name}
            </button>
          ))}
        </div>
        {chosen.length > 0 && (
          <div className="mt-1 text-[11px] text-slate-500">{chosen.length} selected · {chosen.join(", ")}</div>
        )}
      </div>
    );
  }
  // Generic list of free-text values (or list of objects → JSON editor)
  if (field.type === "list") {
    const arr = Array.isArray(value) ? value : [];
    const isObjectList = arr.length > 0 && typeof arr[0] === "object" && arr[0] !== null;
    if (isObjectList || ["components"].includes(fname)) {
      // e.g. Formulation.components — render as JSON textarea
      return (
        <div>
          <Label className="capitalize">{labelText} <span className="text-xs text-slate-400">(list of objects)</span></Label>
          <Textarea
            value={JSON.stringify(arr, null, 2)}
            onChange={(e) => {
              try { onChange(JSON.parse(e.target.value)); } catch { /* keep typing */ }
            }}
            rows={6}
            className="font-mono text-xs"
          />
        </div>
      );
    }
    return (
      <div>
        <Label className="capitalize">{labelText} <span className="text-xs text-slate-400">(list)</span></Label>
        <Input
          value={arr.join(", ")}
          onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          placeholder="comma-separated"
        />
      </div>
    );
  }
  // Nested object — for protocol-rule trigger/recommendations
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return (
      <div>
        <Label className="capitalize">{labelText}</Label>
        <Textarea
          value={JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try { onChange(JSON.parse(e.target.value)); } catch { /* keep typing */ }
          }}
          rows={6}
          className="font-mono text-xs"
        />
      </div>
    );
  }
  // Boolean
  if (typeof value === "boolean" || fname.startsWith("is_") || fname.includes("_compatible") || fname === "is_active") {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <span className="capitalize">{labelText}</span>
      </label>
    );
  }
  // Long text fields
  if (["description", "notes", "kcis_notes", "pregnancy_warnings", "supplement_considerations"].includes(fname)) {
    return (
      <div>
        <Label className="capitalize">{labelText}</Label>
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} />
      </div>
    );
  }
  // Numeric
  if (fname === "priority_score") {
    return (
      <div>
        <Label className="capitalize">{labelText}</Label>
        <Input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} />
      </div>
    );
  }
  // Default: scalar text
  return (
    <div>
      <Label className="capitalize">{labelText}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
